import { createTheme } from "@mui/material";
import { ReadonlyURLSearchParams } from "next/navigation";
import { MenuTab, RaceNo } from "./class/common/base";
import { compareDate, dateFormat, formattedDate, toDate } from "./DayJS";
import { Meeting } from "./class/cms";
import { fakeLoadingPages, menuTabSet } from "./constant/menuTabSet";
import { useRef, useEffect } from 'react';
import { meetingStatus, pmPoolStatus, raceResultStatus, raceStatus } from "./constant/statusSet";
import { getMetaImage, getMetaKeywords, getMetaTitle } from "./Metadata";
import { includes, orderBy } from "lodash";
import { raceTemplate } from "./constant/template";
import { LANGTYPE_EN, LANGCODE_EN, LANGCODE_CT, LangCode } from "./LanguageContext";
import { useMediaQuery } from "@mui/material";
// import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
declare module "@mui/material/styles" {
  interface Theme {
    extraPalette: {
      primary: {
        grey: string;
        darkGrey: string;
      };
      text: {
        primary: string;
      }
    };
  }
  // allow configuration using `createTheme`
  interface ThemeOptions {
    extraPalette?: {
      primary?: {
        grey?: string;
        darkGrey?: string;
      };
      text?: {
        primary?: string;
      }
    };
  }
}

// Remember to sync with globals.scss & variables.module.scss
export const theme = createTheme({
  components: {
    // Name of the component
    MuiButtonBase: {
      defaultProps: {
        // The props to change the default for.
        disableRipple: true, // No more ripple, on the whole application 💣!
      },
    },
    MuiLink: {
      defaultProps: {
        underline: "hover",
        color: "#122C68"
      }
    }
  },
  typography: {
     fontFamily: "inherit",
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1024,
      xl: 1536, 
    },
  },
  palette: {
    primary: {
      main: "#173E96",
      light: "#E2EDF6",
      dark: "#132B68",
    },
    grey: {
      100: "#F4F4F4",
      300: "#E1E1E1",
    },
    text: {
      primary: "#000000",
      secondary: "#333333",
    },
    divider: "#B4B4B4",
  },
  extraPalette: {
    primary: {
      grey: "#909FBD",
      darkGrey: "#6176A0",
    },
    text: {
      primary: "#122C68",
    }
  },
});

const queriesMapping: {[key: string]: string} = {
  racedate: "RaceDate",
  racecourse: "Racecourse",
  raceno: "RaceNo",
};

// return search params in case-insensitive
export const getSearchParams = (searchParams: ReadonlyURLSearchParams | URLSearchParams) => {
  let result: any = {};
  Array.from(searchParams.entries()).forEach(([key, value]) => {
      let mappedKey = key.toLowerCase();
      if (queriesMapping[mappedKey]) {
        mappedKey = queriesMapping[mappedKey]
      }
      result[mappedKey] = value;
  });
  return result;
}

const sanitizeInput = (input?: string) => {
  // Basic sanitation to remove potentially harmful characters
  return input?.replace(/[<>%]/g, ''); // Example: remove <, >, and %
};

/**
 * Need to set isExternal = true when not navigating within /overseas
 * Only input langCodeToChange when switching language
 * 
 * @param newPathname 
 * @param queries 
 * @param keepQuery 
 * @param isExternal 
 * @param langCodeToChange 
 * @returns 
 */
export const generateUrl = (newPathname?: string, queries?: { [key: string]: string | undefined }, keepQuery?: boolean, isExternal: boolean = false, langCodeToChange?: LangCode) => {
  if (newPathname) {
    newPathname = sanitizeInput(newPathname); // Sanitize the pathname
  }

  const path = newPathname?.split("?")?.[0];
  const searchPart = newPathname?.split("?")?.[1];
  let params = new URLSearchParams(keepQuery ? window.location.search : searchPart);
  // sanitation & set param
  // For generating external url, don't apply case insensitive on url query
  const paramsObj = {...(isExternal ? Object.fromEntries(params.entries()) : getSearchParams(params)), ...queries} // queries value will override current params if it is duplicated
  params = new URLSearchParams(paramsObj)
  for (const key in paramsObj) {
    if (paramsObj[key]) {
      params.set(key, sanitizeInput(paramsObj[key]?.toString()) || '');
    } else {
      params.delete(key);
    }
  }
  if (isExternal) {
    return decodeURIComponent(`${path || window.location.pathname}${params.toString() ? "?" + params.toString() : ''}`)
  }
  const langCode = langCodeToChange || window.location.pathname?.split("/")?.[1] || "en-us"
  return decodeURIComponent(`${path ? "/" + langCode + "/overseas" + path : window.location.pathname}${params.toString() ? "?" + params.toString() : ''}`)
}

export const setRouting = (newPathname?: string, queries?: { [key: string]: string | undefined }, curState?: any) => {
  const curUrl = window.location.href
  const host = window.location.host
  const newUrl = generateUrl(newPathname, queries, true);
  console.log('route generate', newUrl, queries)
  if (curUrl.slice(curUrl.indexOf(host) + host.length) != newUrl) {
    console.log('route update', curUrl, newUrl)
    window.history.pushState({ prePath: curUrl }, '', newUrl);
  }
}

export const updateMetaTag = (lang: string, page: string) => {
  console.log('updateMetaTag', lang, page);
  document.title = getMetaTitle(lang, page);
  document.querySelectorAll('meta[name="description"], meta[name="keywords"]')?.forEach(e => e?.setAttribute("content", getMetaKeywords(lang, page)));
  
  // update og tag
  document.querySelectorAll('meta[property="og:title"], meta[name="twitter:title"]')?.forEach(e => e?.setAttribute("content", getMetaTitle(lang, page)));
  document.querySelectorAll('meta[property="og:description"], meta[name="twitter:description"]')?.forEach(e => e?.setAttribute("content", getMetaKeywords(lang, page)));
  document.querySelectorAll('meta[property="og:image"], meta[name="twitter:image"]')?.forEach(e => e?.setAttribute("content", getMetaImage(lang, page)));
}

export const switchLanguage = (lang: LangCode) => {
  const currentPath = window.location.pathname;
  const pathParts = currentPath.split("/");
  if ([LANGCODE_EN, LANGCODE_CT].includes(pathParts[1])) {
    pathParts[1] = lang;
    const newPath = pathParts.splice(0, 4).join("/");
    window.location.href = generateUrl(newPath, {'b_cid': ''}, true, true, lang);
  }
};

export const openTarget = (target?: string | null) => {
  return target ? (["01-NewPage", "_blank"].includes(target || '') ? "_blank" : "_self") : undefined;
};

export const openNewWindow = (newwindow: String | null) => {
  return newwindow === "1" ? "_blank" : "_self";
};

export const redirectToLink = (
  link?: string,
  target?: "_blank" | "_self" | "_parent" | "_top" | "_unfencedTop",
  features?: string
) => {
  if (!link) return;
  return window.open(link, target || "_blank", features);
};

export const defaultValue = <T extends any>(
  src: T | undefined | null,
  defaultValue: T
) => {
  return src ? src : defaultValue;
};

export const isUrlExisted = async (url: string) => {
  let controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), window.globalConfig?.OTHERS?.CHECKEXIST_TIMEOUT || 10000);
  return !url ? false : await fetch(url, { method: "HEAD", cache: "no-store", signal: controller.signal, redirect: 'manual' })
  .then((res) => {return res.url.replace(/https:|http:/g,'') == url.replace(/https:|http:/g,'') && [200, 302].includes(res.status)})
    .catch((err) => {
      console.log('err----', err)
      return false
    });
};

//contentType: 'application/pdf'
export const isUrlExistedWithContentType = async (url: string, contentType: string) => {
  let controller = new AbortController();
  return !url ? false : await fetch(url, { method: "HEAD", cache: "no-store", signal: controller.signal, redirect: 'manual' })
    .then((res) => { 
      const res_content_Type = res.headers.get('content-type');
      //console.log('content-type',res_content_Type,res.status,url);
      return res.url.replace(/https:|http:/g, '') == url.replace(/https:|http:/g, '') && [200, 302].includes(res.status) && res_content_Type?.includes(contentType) })
    .catch((err) => {
      console.log('isUrlExistedWithContentType----', err)
      return false
    });
};

export const paddingNum = (value: Number | number | string, minimumIntegerDigits: number = 2) => {
  switch (typeof value) {
    case 'object':
    case 'number':
      return value.toLocaleString('en-us', { minimumIntegerDigits: minimumIntegerDigits, useGrouping: false })
    case 'string':
      if (!isNaN(Number(value)))
        return Number(value).toLocaleString('en-us', { minimumIntegerDigits: minimumIntegerDigits, useGrouping: false })
  }
  return 0
}

export const getSilkImagePath = (meetingdate: string, meetingVenueCode: string, raceNo: Number | number | string, horseId: number | string) => {
  const year = meetingdate.substring(0, 4);
  const path = `${year}/${meetingdate}_${meetingVenueCode}`;
  const folder = `${meetingdate}_${meetingVenueCode}_r${paddingNum(raceNo)}`
  return `${path}/${folder}/${folder}_h${paddingNum(horseId)}`
}

export const generateSilkImagePath = (path: string, langCode?: string) => {
  let url = `${getCmsDomain()}/-/media/Sites/Common/SimulcastRaceColor/${path}.gif`;
  return generateUrl(url, {
    'sc_lang': langCode || ''
  }, false, true);
};

export const generateFormGuidePdfPath = (langType: string, season: string, meetingdate: string, meetingVenueCode?: string, raceNo?: number | string) => {
  if (!meetingdate) return;
  const fileName = `OS${langType[0]?.toUpperCase() || 'E'}${meetingdate.split('T')[0]}_starter` // chi: 'OSC' / eng: 'OSE'
  const suffix = meetingVenueCode ? `_${meetingVenueCode}${raceNo ? `_r${raceNo}` : '_all'}` : `_all` // 3 types: all races ('_all') / individual meeting ('_{venueCode}_all') / individual race ('_{venueCode}_r{raceNo}')
  const urlPath = '{sitecoreDomain}/-/media/Sites/JCRW/FormGuide/Simulcast/{season}/{meetingdate}/{formGuideFileName}.pdf'
  const url = urlPath
            .replace('{sitecoreDomain}', getCmsDomain())
            .replace('{season}', season)
            .replace('{meetingdate}', meetingdate)
            .replace('{formGuideFileName}', fileName + suffix);
  return url
};

export const formattedNumber = (value: number) => {
  const newFormattedNumber = new Intl.NumberFormat("en-us", {
    style: "decimal",
    currency: "USD",
  }).format(value);
  return newFormattedNumber;
};

export const arrayToObjectByDisplayName = <T extends { displayName: string}>(array: T[]) => {
  return Array.isArray(array) && array.reduce((acc: any, value: T) => ({
    ...acc,
    [value?.displayName]: value
  }), {}) || {};
}

export function redirectPage(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, action: string, lang: string) {
  e.preventDefault();
  let url = "";
  if (typeof window !== "undefined" && window.top !== null) {
    let returnURL = window.top.location.href;
    if (typeof returnURL !== "undefined") {
      returnURL = encodeURIComponent(returnURL);
      if(lang === LANGTYPE_EN){
        if (action == "login") {
          url = window.globalConfig?.LINKED_PAGES?.LOGINPAGE + "Lang=E&ReturnUrl=" + returnURL;
          window.top.location.href = url;
        } else if (action == "logout") {
          alert("If you are using HKJC Web Services in other browser windows, they will also be logged out. Please close those browser windows if needed.");
          url = window.globalConfig?.LINKED_PAGES?.LOGOUTPAGE + "&Lang=E&ReturnUrl=" + returnURL;
          window.top.location.href = url;
        } else if (action == "register") {
          url = window.globalConfig?.LINKED_PAGES?.REGISTERPAGE + "&Lang=E";
          window.top.location.href = url;
        } else if (action == "preference") {
          url = window.globalConfig?.LINKED_PAGES?.PREFERENCEPAGE + "&Lang=E&ReturnUrl=" + returnURL;
          window.top.location.href = url;
        } else if (action == "help") {
          window.open(window.globalConfig?.LINKED_PAGES?.FAQPAGEENG);
        }
      } else {
        if (action == "login") {
          url = window.globalConfig?.LINKED_PAGES?.LOGINPAGE + "Lang=C&ReturnUrl=" + returnURL;
          window.top.location.href = url;
        } else if (action == "logout") {
          alert("如你正在其他視窗開啟馬會網上服務，該等服務亦會同時被登出，如有需要請關閉該等視窗。");
          url = window.globalConfig?.LINKED_PAGES?.LOGOUTPAGE + "&Lang=C&ReturnUrl=" + returnURL;
          window.top.location.href = url;
        } else if (action == "register") {
          url = window.globalConfig?.LINKED_PAGES?.REGISTERPAGE + "&Lang=C";
          window.top.location.href = url;
        } else if (action == "preference") {
          url = window.globalConfig?.LINKED_PAGES?.PREFERENCEPAGE + "&Lang=C&ReturnUrl=" + returnURL;
          window.top.location.href = url;
        } else if (action == "help") {
          window.open(window.globalConfig?.LINKED_PAGES?.FAQPAGECHI);
        }
      }
    }
  }
}

export const generateUrl2SpecifiedMeeting = (lang: LangCode, meeting?: Meeting, raceNo?: string, component?: "_Calendar" | "-Fixture_Race", keepQuery?: boolean) => {
  if (!meeting) return;
  const raceSummaryMenuTab = new MenuTab(menuTabSet.RACE_SUMMARY);
  return generateUrl(`/${raceSummaryMenuTab.valueOf()}`, {
    'RaceDate': formattedDate(toDate(meeting.meetingDate?.value, dateFormat.cms.datetime), 'YYYYMMDD'),
    'Racecourse': meeting.venueCodeForSearchParam(),
    'RaceNo': new RaceNo(raceNo).valueOf() ? new RaceNo(raceNo).toString() : '',
    'redirect': 'Y',
    'b_cid': component ? `SPLDSP_racing/simulcast${component}` : '',
    'focus': 'Y'
  }, keepQuery, false, lang);
};

export const generateHorseProfileUrl = (lang: LangCode, meetingdate: Date, meetingVenueCode: string, raceNo: string, horseId: string, horseNo: string, pageName?: string) => {
  let urlPath = '/{lang}/overseas/horseprofile?h={meetingDate}/{meetingVenueCode}/{raceNo}/{simulcastHorseId}/{horseNo}'
  if (pageName) {
    const internalAd = `SPLDSP_racing/simulcast-${pageName}_HorseProfile`;
    urlPath += '&b_cid=' + internalAd;
  }

  let url = urlPath
    .replace('{lang}', lang)
    .replace('{meetingDate}', formattedDate(meetingdate, "YYYYMMDD"))
    .replace('{meetingVenueCode}', meetingVenueCode || '')
    .replace('{raceNo}', raceNo)
    .replace('{simulcastHorseId}', horseId)
    .replace('{horseNo}', horseNo);
  return url;
};

export const addSeasonYear = (season: string, year: number, connector?: string) => {
  const [prefix, suffix] = season.split(/[^0-9]/g).map((s) => parseInt(s) ? parseInt(s) : 0)
  return `${prefix + year}${connector || season.replaceAll(/\d/g, "")}${suffix + year}`;
}

export const getCmsDomain= () => {
  return window.globalConfig?.DATA_SOURCES?.CMS_DOMAIN;
};

export const getJcbwDomain= () => {
  return window.globalConfig?.DATA_SOURCES?.JCBW_DOMAIN;
};

export const useClickReflesh = (delay = 60*1000) => {
  const timer60second = useRef<any>(null);
  const timerloading = useRef<any>(null);

  const onclickDuringDelay = (loading:any, setLoading:any) => {
    if (loading) return;
    setLoading(true);
    timerloading.current = setTimeout(() => {
      setLoading(false)
    }, 300)
  }

  const onclickRefleshOnce = (setLoading:any, func:any) => {
    timerloading.current = null;
    clearTimeout(timerloading.current);
    setLoading(true);
    func();
  }

  const delay60sRefresh = (loading:any, setLoading:any, func:any) => {
    if (timer60second.current) {
      onclickDuringDelay(loading, setLoading);
      return;
    } else {
      if (timer60second.current) return;
      onclickRefleshOnce(setLoading, func)
      timer60second.current = setTimeout(() => {
        timer60second.current = null;
      }, delay);
    }
  }
  useEffect(() => {
    return () => {
      timerloading.current = null;
      timer60second.current = null;
      clearTimeout(timerloading.current);
      clearTimeout(timer60second.current);
    }
  }, [])
  return {
    delay60sRefresh
  };
};

export const getDefaultMenuTab = (bizDate?: Date, meeting?: Meeting) => {
  let defaultMenuTab = menuTabSet.RACE_SUMMARY;
  if (!bizDate || !meeting) {
    return  new MenuTab(defaultMenuTab)
  }

  const noRacecardMtStatus = [meetingStatus.DRAFT];
  if (compareDate(formattedDate(toDate(meeting.meetingDate?.value, dateFormat.cms.datetime), "YYYY-MM-DD"), formattedDate(bizDate, "YYYY-MM-DD"), "after", true)){
    if (meeting.RaceMeetingProfile?.status && !noRacecardMtStatus.includes(meeting.RaceMeetingProfile?.status?.toUpperCase())) { // biz date <= meeting date + meeting status !== (Draft) *IDA RS state doc: mt status = Defined have complete race card information
      defaultMenuTab = menuTabSet.RACE_CARD
    }
  } else if (meeting?.isPostRace(bizDate)) {
    defaultMenuTab = menuTabSet.RESULTS
  }

  return new MenuTab(defaultMenuTab)
}

export const adobeData = (lang: "en"|"zh-hk" | "zh-cn", type: "pageview" | "interaction", data: WAData, defaultRaceNo = true) => {
  // Refer to: https://deopconf01.corp.hkjc.com/display/RWWS/JCRW+Simulcast+Implementation+Guide
  /*
    adobeDataLayer.push({
      "event": "pageview", // options: pageview / interaction
      "page": {
        "name": "<use url for pagename if here is empty>", //Optional
        "id": "racing/simulcast",
        "language": "<en/zh-hk/zh-cn>",
      },
      "user": {
        "cuno": "cf12358c-af1b-4aea-b4c0-xxxxxxxxxxxx",
        "customer_segment": "N00000NNN0061YY",
      },
      "content": {
        "horse": "20240911-S1-God",   // <Date>-<VenueCode>-<HorseName in Eng>
        "race_no": "20230313-S1-3",   // "<Date> - <VenueCode> - <RaceNo.> e.g. 20230313-S1-3(NA if no value, e.g. 20230313-NA-NA)"
      },
      "interaction": {
        "sorting": "by name desc",
        "event_id": "racing/simulcast_Click_RaceSummary_HighlightComponent",
        "button_name": "racing/simulcast_Click_All",
        "video_filename": "<Akamai link - Naming conversion>",
        "content_title": "<File Title> / <Video Title>",
        "video_source": "",
      },
      "report_suite": "", // leave empty for now
      "internal_ad": "SPLDSP_Member/Portal/EMP/Home_spotlight"
    });
  */
    const adobeData = {
      "event": type,
      "page": {
        "name": data.pageUrl,
        "id": data.pageId,
        "language": lang,
      },
      "user": {
        "cuno": window?.WCIPCookie ? window?.WCIPCookie?.getCuno() : "",
        "customer_segment": window?.WCIPCookie ? window?.WCIPCookie?.getCustomerSegment() : "",
      },
      "content": {
        "horse": data.horse || "",
        "race_no": data.raceNo || (defaultRaceNo ? "NA-NA-NA" : ""),
        "betType": data.bettype || "", 
        "author": data.author || "",
        "country": data.country || ""
      },
      "interaction": {
        "sorting": data.sorting || "",
        "event_id": type === 'pageview' ? '' : data.eventId || "",
        "button_name": data.buttonName || "",
        "video_filename": data.videoFileName || "",
        "content_title": data.contentTitle || "",
        "video_source": data.videoSource || "",
        "download_filename": data.downloadFileName || ""
      },
      "report_suite": "",
      "internal_ad": decodeURIComponent(data.internalAd || ""),
    }

  
  const pushData = (isEU?: boolean) => {
    try {
      adobeData.user.cuno = window?.WCIPCookie?.getCuno()
      adobeData.user.customer_segment = window?.WCIPCookie?.getCustomerSegment()

      console.log("adobeData->pushData - trigger adobeData", adobeData, "isEU", isEU);
      window?.adobeDataLayer?.push({
        "event": "watracking",
        "IsEU": isEU,
      });
      window?.adobeDataLayer?.push(adobeData);      
    } catch (e) {
      console.error("adobeData->pushData error" + e)
    } 
  };

  if (window.WAGdpr && window.adobeDataLayer) {
    const isEU = window.WAGdpr.isEU?.();
    pushData(isEU);
    return;
  }  

  let attempts = 0;
  const maxAttempts = 5; // ~1.5 seconds if interval = 300ms

  const interval = setInterval(() => {
    attempts++;

    if (window.WAGdpr && window.adobeDataLayer) {
      console.log("adobeData - push adobeData on attemps: ", attempts);
      clearInterval(interval);
      const isEU = window.WAGdpr.isEU?.();
      pushData(isEU);
    } else if (attempts >= maxAttempts) {
      clearInterval(interval);
      console.warn("adobeData - WAGdpr/adobeDataLayer not available after attemps:", attempts, ", cannot push adobeData: ", {adobeDataLayer: window.adobeDataLayer, isEU: window.WAGdpr ? window.WAGdpr.isEU() : undefined, adobeData});
    }
  }, 300);
}

export const generateAdobeDataRaceNo = (date?: Date, venueCode?: string, raceNo?: number) => `${defaultValue(formattedDate(date, "YYYYMMDD"),"NA")}-${defaultValue(venueCode,"NA")}-${defaultValue((raceNo && raceNo > 0) ? raceNo?.toString() : undefined,"NA")}`

export const generateAdobeDataHorseName = (date?: Date, venueCode?: string, horseName?: string) => `${defaultValue(formattedDate(date, "YYYYMMDD"),"NA")}-${defaultValue(venueCode,"NA")}-${defaultValue(horseName,"All")}`

export const textFormatAmountFn = (text:any, num = 2, reUnd = true, reZero = false) => {
  if (text !== 0 && (!text || isNaN(text))) return reUnd ? undefined : reZero ? 0 : text;
  let newNum = text - 0;
  return newNum.toLocaleString('en-us', { minimumFractionDigits: num, maximumFractionDigits: num });
};

export const isShowAllTabs = () => {
  const { showalltabs } = getSearchParams(new URLSearchParams(window.location.search));
  return showalltabs ? ['y', 'true'].includes(showalltabs?.toLowerCase()) : false;
};

export const orderMtgWithMainRaceFromCMS = (mtgs: any) => {
  return orderBy(mtgs?.map((x:any)=>({...(x.meeting||x), mainRaceOrder: new Meeting(x.meeting||x)?.mainRace ? 1 : 2})), ['mainRaceOrder', 'meetingVenueCode.value']);
}

export const orderRaceOnBizDateFromQIDS = (meetings: IRaceMeetingProfile[]) => {
  const defaultCurRaceNo = (races: any, no?: number) => no ? no : races?.[0]?.no;
  const hasActiveMtg = meetings?.some((mt: any)=> mt.status && ![meetingStatus.ABANDON, meetingStatus.ABANDON_PARTIAL, meetingStatus.CLOSED].includes(mt.status));
  const mtgStatusOrder = (mtgStatus?: string) => {
    if (!mtgStatus) { 
      return 999;
    } else if ([meetingStatus.CLOSED, meetingStatus.ABANDON, meetingStatus.ABANDON_PARTIAL].includes(mtgStatus)){
      return 2;
    } else {
      return 1;
    }
  }
  const raceStatusOrder = (rStatus?: string, resStatus?: string) => {
    if (!rStatus) { 
      return 999;
    } else if (rStatus === raceStatus.RESULT) {
      return [raceResultStatus.PARTIAL, raceResultStatus.COMPLETED].includes(resStatus || '') ? 1 : 2;
    } else if ([raceStatus.VOIDED, raceStatus.ABANDONED].includes(rStatus)){
      return 2;
    } else {
      return 1;
    }
  }
  const races = meetings?.reduce((acc: any, cur: IRaceMeetingProfile)=>{
    const race = cur?.races?.find((r: any) => r.no === defaultCurRaceNo(cur?.races, cur.currentNumberOfRace));
    if (race) {
      acc.push({
        venueCode: cur.venueCode,
        date: cur.date,
        ...race,
        mtgStatus: cur.status,
        mtgOrder: mtgStatusOrder(cur?.status?.toUpperCase()),
        raceOrder: raceStatusOrder(race?.status?.toString()?.toUpperCase(), race?.raceResults?.[0]?.status?.toUpperCase()),
      })
    }
    return acc;
  }, []);
  const orderedRaces = orderBy(races, ["mtgOrder", "raceOrder", "postTime", "venueCode"], ["asc", "asc", hasActiveMtg ? "asc": "desc", "asc"]);
  return orderedRaces;
}

export const getAdditionalInfoForFixture = (lang: LangCode, fixtureItem: IFixture, component?: "_Calendar" | "-Fixture_Race") => {
  // redirectionLink logic:
  // 1. mapped with race: mapped race
  // 2. mapped with meeting: 
  //     - Before raceday: w/ main race: main race, w/out main race: first race of the mapped meeting
  //     - On raceday: first race of the mapped meeting: raceday date = biz date
  //     - During raceday: mapped meeting has current race running: mapped meeting current race, 
  //         mapped meeting no current race running: upcoming race of the mapped meeting
  //         *meeting status = STARTED
  //     - Raceday meeting finished: last race of the mapped meeting
  //         *meeting status = ABANDON | ABANDON_PARTIAL | CLOSED
  //     - After raceday: same with "Before raceday":  raceday date != biz date & meeting status != STARTED
  const isRace = fixtureItem.meetingRaceMapping?.targetItem?.template?.name == raceTemplate.NAME;

  let raceNo: string | number = 0;
  let mappedMeeting = null;
  let raceDatetime = {};

  if (isRace) {
    mappedMeeting = new Meeting(fixtureItem.meetingRaceMapping?.targetItem?.parent?.meeting);
    raceNo = fixtureItem.meetingRaceMapping?.targetItem?.displayName ?? 1;
    raceDatetime = { value: mappedMeeting?.races?.[0]?.raceDatetime?.value } // PRJ2555-2134 - alwasy show first race datetime
  } else {
    mappedMeeting = new Meeting(fixtureItem.meetingRaceMapping?.targetItem);
    const races = orderBy(fixtureItem.meetingRaceMapping?.targetItem?.races?.[0]?.raceInfo, (race: any) => new RaceNo(race.displayName), ['asc']) || [];
    raceDatetime = { value: races[0]?.raceDatetime?.value } // PRJ2555-957 - display first race
  };
  return {
    redirectLink: generateUrl2SpecifiedMeeting(lang, mappedMeeting, raceNo.toString(), component),
    raceDate: toDate(mappedMeeting.meetingDate?.value, dateFormat.cms.datetime),
    racecourse: mappedMeeting.venueCodeForSearchParam(),
    raceNo: new RaceNo(raceNo).valueOf() ? new RaceNo(raceNo).toString() : '',
    raceDatetime
  };
}

export const getLoginInfo= (custProIn: any, langType: string) => {

  const custProInList = getCookie(custProIn)
  let isLogin = false;
  let displayName = ""
  console.log("custProIn 1", custProIn, custProInList)
  //setIsLogin(false);
  if (custProIn) {
    //setIsLogin(true);
    isLogin = true;
    displayName = langType === LANGTYPE_EN ? 
      showDisplayNameEN(getDisplayName(custProInList), getSalutation(custProInList), getLastName(custProInList), getLastNameChinese(custProInList)) : 
      showDisplayNameCH(getDisplayName(custProInList), getSalutation(custProInList), getLastName(custProInList), getLastNameChinese(custProInList))
    //setShowDisplayName(displayName);   
    console.log("custProIn showDisplayName", displayName)
  }
  return{isLogin, displayName}
  

  function getCookie(name: string) {
    // var nameEQ = name + "=";
    // var ca = document.cookie.split(';'); 
    // let d = "";
    const h = ":";
    // for (var i = 0; i < ca.length; i++) {
    //   var c = ca[i];

    //   while (c.charAt(0) == ' ') c = c.substring(1, c.length);
    //   console.log("ssssss  2",c)
    //   if (c.indexOf(nameEQ) == 0)
    //   { console.log("ssssss  2",nameEQ.length, c.length ,  c.substring(nameEQ.length, c.length)); 
    //    d = c.substring(nameEQ.length, c.length); break}    
    // }
    // console.log("ssssss",d)
    let a = [];
    if (name != null) {
      a = name.split(h);
      return a
    }
    return [];
  }

  function E(a: any[], n: number) {
    if (a?.[n] != null) {
      return a[n].replace(/\+/g, " ") //decodeURIComponent()
    } else {
      return null
    }
  }

  function getDisplayName(a: any[]) {
    return E(a, 0)
  } 
  function getSalutation(a: any[]) {
    return E(a, 1)
  }
  function getLastName(a: any[]) {
    return E(a, 2)
  }
  function getLastNameChinese(a: any[]) {
    return E(a, 3)
  } 
  function showDisplayNameCH(displayname: string|null, salutation: string|null, lastname: string|null, lastnamechinese: string|null) {
    var showname = displayname;
    if (salutation == "Mr." || salutation == "Mr")
      salutation = "先生";

    if (salutation == "Ms." || salutation == "Ms")
      salutation = "女仕";

    if (salutation == "Miss." || salutation == "Miss")
      salutation = "小姐";

    if (salutation == "Mrs." || salutation == "Mrs")
      salutation = "太太";

    if (displayname == "" || displayname == null) {

      if (lastnamechinese == "" || lastnamechinese == null)
        showname = (lastname ?? "") + (salutation ?? "");
      else
        showname = lastnamechinese + salutation;
    }

    var name = "    " + showname + "    ";
    return name;
  }

  function showDisplayNameEN(displayname: string|null, salutation: string|null, lastname: string|null, lastnamechinese: string|null) {
    var showname = displayname;
    if (salutation == "先生")
      salutation = "Mr";

    if (salutation == "女士")
      salutation = "Ms";

    if (salutation == "小姐")
      salutation = "Miss";

    if (salutation == "太太")
      salutation = "Mrs";

    if (displayname == "" || displayname == null)
      showname = salutation + "." + lastname;

    var name = "    " + showname + "    ";
    return name;
  }
}

export const setRacesDetailLoading = (setLoadingState:(loading: boolean) => void, menuTabStr?: string, isLoading?: boolean, requiredFakeLoading?: boolean) => {
  if (isLoading) {
    setLoadingState(true);  
    if (requiredFakeLoading && fakeLoadingPages.includes(menuTabStr || '')){
      setTimeout(() => {
        setLoadingState(false);
      }, 300)
      return
    }
  } else {
    setLoadingState(false);
  }
}

export const isBeforeCutoff = (date?: Date) => (date && date.getTime() !== 0) ? compareDate(formattedDate(date, "YYYYMMDD"), window.globalConfig?.OTHERS?.CUTOFF_DATE, "before", false) : false;

export const parseBetline = (betSelectionName: string, betSelectionDetail: string) => {
  let parsedObj: DoubleTrioGroup | DoubleOrTrebleGroup | null = null;

  const DT_HEADER_RE = /(S\d{1,2})-(\d{1,2}):?\s+(.+)/i;
  const TOKEN_START_RE = /(?:^|[,\n\/，、]|\band|及\b|&)\s*(\d+)[.．]?\s*/gi;

  function normalizeInput(text: string) {
    if (!text) return '';
    // Accept pasted escape sequences; normalize to \n
    let t = text
      .replace(/\\r\\n/g, '\n')
      .replace(/\\n/g, '\n')
      .replace(/\\t/g, '\t')
      .replace(/\r\n?/g, '\n');
    return t;
  }

  function parseHorseList(line: string) {
    if (!line) return [];
    const out: HorsePick[] = [];
    const matches = [];

    // Gather all token start matches with their absolute indices
    let m;
    TOKEN_START_RE.lastIndex = 0;
    while ((m = TOKEN_START_RE.exec(line)) !== null) {
      matches.push({
        idx: m.index,
        num: Number(m[1]),
        // Absolute index where the name content starts (right after the matched '<num>. ')
        nameStart: TOKEN_START_RE.lastIndex
      });
    }
    if (!matches.length) return out;

    // Determine the end boundary for each name (next token start, or end of line)
    for (let i = 0; i < matches.length; i++) {
      const cur = matches[i];
      const next = matches[i + 1];
      const nameEnd = next ? next.idx : line.length;
      let name = line.slice(cur.nameStart, nameEnd);

      // Clean trailing separators/punctuation/whitespace
      name = name.replace(/[\s,\/&]+$/g, '').trim();

      out.push({ horseNum: cur.num, horseName: name });
    }
    return out;
  }

  function parseDoubleOrTreble(input: string) {
    const text = normalizeInput(input);
    const lines = text.split('\n');

    const headerRe = /([A-Z0-9]+)-(\d+)\s*(.+)?/i; // capture possible type (e.g., Multiple)
    let currentHeader: any = null;
    let currentPicks: any[] = [];
    const out: any[] = [];

    function flush() {
      if (currentHeader && currentPicks.length > 0) {
        const rec = {
          venueCode: currentHeader.venueCode,
          raceNumber: currentHeader.raceNumber,
          combination: currentPicks,
          type: ""
        };
        if (currentHeader.type) rec.type = currentHeader.type;
        out.push(rec);
      }
      currentHeader = null;
      currentPicks = [];
    }

    for (const rawLine of lines) {
      const line = rawLine ?? '';
      const mh = line.match(headerRe);
      if (mh) {
        // Flush previous block
        flush();
        const venueCode = mh[1].toUpperCase();
        const raceNumber = Number(mh[2]);
        let type = (mh[3] || '').trim();
        if (/^\s*Multiple\s*$/i.test(type)) type = 'Multiple';
        else if (/^\s*Banker\s*$/i.test(type)) type = 'Banker';
        else if (type === '') type = '';

        currentHeader = { venueCode, raceNumber, type };
        continue;
      }
      if (currentHeader) {
        const picks = parseHorseList(line);
        if (picks.length) currentPicks.push(...picks);
      }
    }
    flush();
    return out;
  }

  function parseDoubleTrioFrom(lines: string[], startIndex: number) {
    const result: any = {
      venueCode: null,
      raceNumber: null,
      type: null,      // always null for Double Trio
      banker: null,    // array or null
      selections: []
    };

    let i = startIndex;

    // Find the first header like: S2-6    Selections | S2-6    Banker
    for (; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      const m = line.match(DT_HEADER_RE);
      if (!m) continue;

      result.venueCode = m[1];
      result.raceNumber = Number(m[2]);
      const tag = m[3].toLowerCase();
      i++;

      if (tag === 'selections' || tag === 'multiple' || tag === '複式' || tag === '配腳') {
        // Next non-empty line is the selection list
        while (i < lines.length && !lines[i].trim()) i++;
        if (i < lines.length) result.selections = parseHorseList(lines[i].trim());
        return { result, nextIndex: i + 1 };
      }

      // tag === 'banker'
      while (i < lines.length && !lines[i].trim()) i++;
      if (i < lines.length) {
        const lst = parseHorseList(lines[i].trim());
        result.banker = lst.length ? lst : null;
      }
      i++;

      // Expect a Selections header next (either "Selections" or "S2-6    Selections")
      while (i < lines.length) {
        const l2 = lines[i].trim();
        if (!l2) { i++; continue; }

        if (/\s*(Selections|Sel\.|配腳)/i.test(l2)) {
          i++;
          while (i < lines.length && !lines[i].trim()) i++;
          if (i < lines.length) result.selections = parseHorseList(lines[i].trim());
          return { result, nextIndex: i + 1 };
        }

        const m2 = l2.match(DT_HEADER_RE);
        if (m2 && /(Selections|Sel\.|配腳)/i.test(normalizeInput(m2[3]))) {
          i++;
          while (i < lines.length && !lines[i].trim()) i++;
          if (i < lines.length) result.selections = parseHorseList(lines[i].trim());
          return { result, nextIndex: i + 1 };
        }

        // If it's something else, keep scanning within the same block until we find Selections.
        i++;
      }
      return { result, nextIndex: i };
    }

    // No header found from startIndex onward
    return { result, nextIndex: i };
  }

  function parseDoubleTrio(text: string) {
    const lines = text.split(/\r?\n/);
    const results = [];
    let i = 0;

    while (i < lines.length) {
      // Skip until we find a DT header
      let found = false;
      for (; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;
        if (DT_HEADER_RE.test(line)) { found = true; break; }
      }
      if (!found) break;

      const { result, nextIndex } = parseDoubleTrioFrom(lines, i);
      // Only push if we actually captured a venue + race (valid block)
      if (result.venueCode && result.raceNumber != null) {
        results.push(result);
      }
      // Move forward; guard against infinite loops
      i = Math.max(nextIndex, i + 1);
    }
    return results;
  }

  switch (true) {
    case betSelectionName.includes("DOUBLE TRIO") || betSelectionName.includes("孖T"):
      parsedObj = {
        betType: "D-T",
        bet: parseDoubleTrio(betSelectionDetail)
      };
      break;
    case betSelectionName.includes("DOUBLE") || betSelectionName.includes("孖寶"):
      parsedObj = {
        betType: "DBL",
        bet: parseDoubleOrTreble(betSelectionDetail)
      };
      break;
    case betSelectionName.includes("TREBLE") || betSelectionName.includes("三寶"):
      parsedObj = {
        betType: "TBL",
        bet: parseDoubleOrTreble(betSelectionDetail)
      };
      break;
    default:
      break;
  }

  return parsedObj;
}

/**
 * （N日 / N小時 / N分鐘）
 * @param {number} seconds 
 * @returns {string} 
 */
export const formatDuration = (seconds: any, langCode: string, t: any, raceStatu: any, isJumped: any) => {
  // if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds < 0) {
  //   throw new TypeError('seconds must be a non-negative finite number');
  // }
  const secPerMin = 60;
  const secPerHour = 3600;
  const secPerDay = 86400;
  const s = Math.floor(seconds);

  const dayText = t("upcoming_day")
  const hourText = t("upcoming_hour")
  const minText = t("upcoming_min")
  const soon = t("upcoming_Soon")
  switch (raceStatu) {
    case "ABANDONED":
      return t("upcoming_abandoned")
    case "VOIDED":
      return t("upcoming_voided")
    // case "RESULT":
    // case "CLOSED":
    //   return t("upcoming_stopsell")
    default:

      if (s >= secPerDay) {
        const days = Math.floor(s / secPerDay);
        return `${days}${dayText}`;
      }

      if (s >= secPerHour) {
        const hours = Math.floor(s / secPerHour);
        const minutes = Math.floor((s % secPerHour) / secPerMin);
        return `${hours}${hourText} ${minutes}${minText}`;
      }

      if (s >= secPerMin) {
        const minutes = Math.floor(s / secPerMin);
        return `${minutes}${minText}`;
      }
      if (s < secPerMin && s > 0) {
        return 0 + minText
      }
      if (s <= 0 && isJumped) {
        return t("upcoming_stopsell")
      }
      return soon;
  }
}

export const openSingleTab =(url: any, winRef: any) => {
  if (winRef.current && !winRef.current.closed) {
      winRef.current.location.href = url
      winRef.current.focus()
    } else {
      winRef.current = window.open(url, 'SINGLE_TAB')
    }
}

export const useViewTracker = (onValidView: () => void, delay: number = 5000): React.RefObject<HTMLDivElement | null> => {
  const elementRef = useRef<HTMLDivElement | any>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasTracked = useRef<boolean>(false);
 
  useEffect(() => {
    if (hasTracked.current) return;
 
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          console.log("%c[WA Test] Start 5s countdown...", "color: #4CAF50; font-weight: bold;");
 
          let secondsLeft = delay / 1000;
 
          intervalRef.current = setInterval(() => {
            secondsLeft -= 1;
            if (secondsLeft > 0) {
              console.log(`%c[WA Test] Remaining ${secondsLeft}s...`, "color: #2196F3;");
            }
          }, 1000);
 
          timerRef.current = setTimeout(() => {
            if (!hasTracked.current) {
              console.log("%c[WA Test] ✅ Success!", "color: #FF9800; font-weight: bold;");
              onValidView();
              hasTracked.current = true;
              if (intervalRef.current) clearInterval(intervalRef.current);
              observer.disconnect();
            }
          }, delay);
        } else {
          if (timerRef.current || intervalRef.current) {
            console.log("%c[WA Test] ❌ Reset", "color: #f44336;");
            if (timerRef.current) {
              clearTimeout(timerRef.current);
              timerRef.current = null;
            }
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
          }
        }
      },
      { threshold: 0.5 },
    );
 
    if (elementRef.current) {
      observer.observe(elementRef.current);
    }
 
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
      observer.disconnect();
    };
  }, [onValidView, delay]);
 
  return elementRef;
}; 

  // for xiaomi compatible SQ0253-1721
export const openInNewTab = (url: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer'; // Essential for security and performance
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

export const isIOS = () => {
  const ua = navigator.userAgent;
  return /Mac|iPhone|iPad|iPod/.test(ua) ||
         (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

// more precise Interval
export const useInterval = (
  callback: (elapsed?: number) => void,
  active: boolean = true) =>
 {
  const savedCallback = useRef(callback);
  const startTime = useRef<number>(0);
  const timerId = useRef<number>(0);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    const delay =  window.globalConfig.UPCOMINGCONFIG.INTERVAL_TIME*1000;
    if (!active || delay == null) {
      clearTimeout(timerId.current);
      return;
    }

    startTime.current = Date.now();
    function tick() {
      const now = Date.now();
      const elapsed = now - (startTime.current || now);
      savedCallback.current(elapsed);
      const drift = elapsed % delay;

      timerId.current = window.setTimeout(
        tick,
        delay - drift
      );
    }

    timerId.current = window.setTimeout(tick, delay);

    return () => {
      if (timerId.current) {
        clearTimeout(timerId.current);
      }
    };
  }, [active]);
}

export function useBodyScrollLock(open: boolean) {
  const isMobile = useMediaQuery(theme.breakpoints.down("lg")) ?? false;
  useEffect(() => {
    if (isMobile && !isIOS()) {
      if (open) {
        document.documentElement.style.overflowY = "hidden"
      } else {
        document.documentElement.style.overflowY = '';
      }
    }
    return () => {
       document.documentElement.style.overflowY = '';
    };
  }, [open]);
}
